import type { BookingResultResponse } from '@/components/BookingResultView';

/**
 * Default conflict copy shown when the backend doesn't supply its own text.
 * Never surfaced: raw JSON, HTTP status codes, or developer messages.
 */
const DEFAULT_CONFLICT: BookingResultResponse = {
  success: false,
  bookingConfirmed: false,
  title: 'Zeitslot bereits vergeben',
  message:
    'Leider wurde dieser Zeitslot während des Buchungsvorgangs von einem anderen Spieler wenige Augenblicke vorher reserviert. Deshalb konnten wir Ihre Buchung nicht mehr bestätigen.',
  nextStep: 'Bitte wählen Sie einen anderen verfügbaren Termin aus.',
};

const DEFAULT_UNKNOWN: BookingResultResponse = {
  success: false,
  bookingConfirmed: false,
  title: 'Buchungsstatus konnte nicht eindeutig geprüft werden',
  message:
    'Falls Sie eine Zahlungsbestätigung erhalten haben, kontaktieren Sie bitte den SV Heinersreuth. Wir prüfen Ihre Buchung umgehend.',
};

/** True if the string looks like it contains raw technical content. */
function looksLikeTechnical(s: string): boolean {
  return (
    s.includes('{') ||
    s.includes('}') ||
    s.includes('HTTP') ||
    /\b\d{3}\b/.test(s) ||        // any 3-digit status code
    s.toLowerCase().includes('capture') ||
    s.toLowerCase().includes('fehlgeschlagen (') ||
    s.toLowerCase().includes('webhook') ||
    s.toLowerCase().includes('stack') ||
    s.toLowerCase().includes('error:')
  );
}

/**
 * Safely extract a JSON object from a fetch Response whose status >= 400.
 * Returns null if the body is not valid JSON or is empty.
 */
export async function parseErrorResponse(res: Response): Promise<BookingResultResponse | null> {
  try {
    const text = await res.text();
    if (!text) return null;
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') return parsed as BookingResultResponse;
  } catch {
    // not JSON — ignore
  }
  return null;
}

/**
 * Given a raw backend response object (possibly from a failed/conflict request),
 * return a safe, user-friendly BookingResultResponse.
 *
 * Rules:
 * - success + bookingConfirmed → success passthrough
 * - success === false OR bookingConfirmed === false OR HTTP 409 → conflict
 * - Anything else → unknown
 * - Raw technical strings in title/message are replaced with safe defaults
 */
export function normalizeBookingResult(
  raw: Partial<BookingResultResponse> | null | undefined,
  options: {
    httpStatus?: number;
    /** true = a payment WAS charged (Stripe/PayPal), false = free booking */
    paymentCharged?: boolean;
    paymentRefunded?: boolean;
  } = {}
): BookingResultResponse {
  const { httpStatus, paymentCharged = false, paymentRefunded } = options;

  // Explicit success
  if (raw?.success === true && raw?.bookingConfirmed !== false) {
    return {
      success: true,
      bookingConfirmed: true,
      title: raw.title ?? 'Buchung erfolgreich',
      message: raw.message ?? 'Ihre Buchung wurde bestätigt. Eine Bestätigungs-E-Mail ist unterwegs.',
    };
  }

  // Detect conflict: explicit flag, bookingConfirmed false, or HTTP 409
  const isConflict =
    raw?.success === false ||
    raw?.bookingConfirmed === false ||
    httpStatus === 409;

  if (isConflict) {
    const refunded = paymentRefunded ?? raw?.paymentRefunded;
    const wasCharged = refunded !== undefined ? true : paymentCharged;

    // Use backend-supplied copy only if it doesn't look technical
    const title =
      raw?.title && !looksLikeTechnical(raw.title)
        ? raw.title
        : DEFAULT_CONFLICT.title!;

    const message =
      raw?.message && !looksLikeTechnical(raw.message)
        ? raw.message
        : DEFAULT_CONFLICT.message!;

    const nextStep =
      raw?.nextStep && !looksLikeTechnical(raw.nextStep)
        ? raw.nextStep
        : DEFAULT_CONFLICT.nextStep;

    let refundMessage: string | undefined;
    if (refunded === true) {
      refundMessage =
        raw?.refundMessage && !looksLikeTechnical(raw.refundMessage)
          ? raw.refundMessage
          : 'Ihre Zahlung wurde automatisch und vollständig zurückerstattet.';
    } else if (!wasCharged || refunded === false) {
      refundMessage = 'Es wurde keine Zahlung belastet.';
    }

    return {
      success: false,
      bookingConfirmed: false,
      paymentRefunded: refunded,
      title,
      message,
      refundMessage,
      nextStep,
    };
  }

  // Unknown / ambiguous
  return {
    ...DEFAULT_UNKNOWN,
    paymentRefunded: paymentRefunded ?? raw?.paymentRefunded,
  };
}
